import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCoreEvent } from '@services/core';
import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  mapNotification,
  type ApiNotification,
} from '../core/api/notifications';
import type { Notification, UserRole } from '@shared/types';
import { NOTIFICATION_EVENTS } from './notification-events';

export const LIVE_NOTIFICATIONS_QUERY_KEY = ['notifications', 'live'] as const;

/**
 * Single source for "fetch once + stay live via socket + mark read" — used by both
 * the TopBar dropdown and the full NotificationsPage so the two never drift apart.
 * Socket push sends the raw backend shape (uuid/recipientRole), same as the REST
 * list — always go through mapNotification() before touching FE state.
 *
 * Lives in @services (not @modules/notifications) so app chrome (TopBar) can consume
 * it downward without a shared→module cycle; @modules/notifications re-exports it.
 *
 * Uses React Query (not useEffect) so remounting AppLayout/TopBar on route change
 * does not re-hit GET /v1/notifications — cache is shared above the page shell.
 */
export function useLiveNotifications(role: UserRole) {
  const queryClient = useQueryClient();
  const queryKey = [...LIVE_NOTIFICATIONS_QUERY_KEY, role] as const;

  const { data: items = [], isLoading: loading } = useQuery({
    queryKey,
    queryFn: listNotifications,
    // AppLayout remounts on every route; keep the list warm across navigations.
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  useCoreEvent<ApiNotification>(NOTIFICATION_EVENTS.NEW, (raw) => {
    const incoming = mapNotification(raw);
    if (incoming.role && incoming.role !== role) return;
    queryClient.setQueryData<Notification[]>(queryKey, (prev = []) =>
      prev.some((n) => n.id === incoming.id) ? prev : [incoming, ...prev],
    );
  });

  const unreadCount = items.reduce((count, n) => count + (n.read ? 0 : 1), 0);

  const markRead = (id: string) => {
    void markNotificationRead(id).catch(() => {});
    queryClient.setQueryData<Notification[]>(queryKey, (prev = []) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
  };

  const markAllRead = () => {
    void markAllNotificationsRead().catch(() => {});
    queryClient.setQueryData<Notification[]>(queryKey, (prev = []) =>
      prev.map((n) => ({ ...n, read: true })),
    );
  };

  return { items, loading, unreadCount, markRead, markAllRead };
}
